"use strict";

const _ = require('lodash');
const d3 = require('d3');
const mix = require('../lib/mix');
const axis = require('../lib/axis');
const YAxis = axis.YAxis;
const XAxis = axis.XAxis;
const legend = require('../lib/legend');
const EventEmitter = require('events').EventEmitter;

class BasicChart extends EventEmitter {
	constructor(options) {
		super();
		this._options = Object.assign({
			margin: {top: 30, right: 70, bottom: 50, left: 20},
			height: 240, width: null, xKey: 'key', yKey: 'value'
		}, options);
		_.bindAll(this, 'getX', 'getY');
	}

	get options() {
		return this._options;
	}

	get data() {
		return this._data;
	}

	get node() {
		return this._node;
	}

	getOrCall(key, d, i) {
		if (_.isFunction(key)) {
			Array.prototype.splice.call(arguments, 0, 2, this);
			return key.apply(null, arguments);
		}

		let v = _.get(d, key);
		if (_.isFunction(v)) {
			Array.prototype.splice.call(arguments, 0, 2, this);
			return v.apply(d, arguments);
		}
		return v;
	}

	getFromOptionsOrInit(prop, init) {
		let v = _.get(this.options, prop);
		if (_.isUndefined(v)) {
			v = init.apply(this, Array.prototype.slice.call(arguments, 2));
		}
		return v;
	}

	getX(d, i) {
		return this.getOrCall(this.options.xKey, d, i);
	}

	getY(d, i) {
		return this.getOrCall(this.options.yKey, d, i) || 0;
	}

	calculateDataDomain(data, getter) {
		let l = data && data.length;
		if (!l) {
			return [];
		}
		let min = Infinity;
		let max = -Infinity;
		for (let i = 0; i < l; i++) {
			let d = data[i];
			let v = getter.call(this, d, i);
			max = Math.max(v, max);
			min = Math.min(v, min);
		}
		return [min, max];
	}

	calculateXDomain() {
		// TODO: We do not support range domain for x axis.
		// TODO: Consider implementing something like this: https://runkit.com/sunrize531/building-xdomain-for-d3
		return _(this.data).map(this.getX).uniq().sortedUniq().value();
	}

	// TODO: if user gives scales or axis via options - we need to get domain from axis.
	get xDomain() {
		if (!this._xDomain) {
			this._xDomain = this.getFromOptionsOrInit('xDomain', this.calculateXDomain);
		}
		return this._xDomain;
	}

	initXScale() {
		return d3.scaleBand()
			.rangeRound([0, this.options.innerWidth], 0.15)
			.padding(0.15).align(0.1)
			.domain(this.xDomain);
	}

	get xScale() {
		if (!this._xScale) {
			this._xScale = this.getFromOptionsOrInit('xScale', this.initXScale);
		}
		return this._xScale;
	}

	initXAxis() {
		let options = this.options;
		return new XAxis(
			this.xScale,
			{
				width: options.innerWidth,
				translate: [0, options.innerHeight],
			}
		);
	}

	drawXAxis(selection) {
		this._xAxis = this.getFromOptionsOrInit('xAxis', this.initXAxis);
		this._xAxis.draw(selection);
		return this._xAxis;
	}

	get xAxis() {
		return this._xAxis;
	}

	calculateYDomain() {
		// Always start with 0, we don't like negative values here.
		return [0, _(this.data).map(this.getY).max()];
	}

	// TODO: if user gives scales or axis via options - we need to get domain from axis.
	get yDomain() {
		if (!this._yDomain) {
			this._yDomain = this.getFromOptionsOrInit('yDomain', this.calculateYDomain);
		}
		return this._yDomain;
	}

	initYScale() {
		let options = this.options;
		return d3.scaleLinear()
			.rangeRound([options.innerHeight, 0])
			.domain(this.yDomain).nice();
	}

	get yScale() {
		if (!this._yScale) {
			this._yScale = this.getFromOptionsOrInit('yScale', this.initYScale);
		}
		return this._yScale;
	}

	initYAxis() {
		let options = this.options;
		return new YAxis(
			this.yScale,
			{
				width: options.innerWidth,
				height: options.innerHeight,
				ticks: 5
			}
		);
	}

	drawYAxis(selection) {
		this._yAxis = this.getFromOptionsOrInit('yAxis', this.initYAxis);
		this._yAxis.draw(selection);
		return this._yAxis;
	}

	get yAxis() {
		return this._yAxis;
	}

	drawChart(selection, options) {
		this._chart = selection;
		return this._chart;
	}

	prepareOptions(options) {
		options = Object.assign({}, this.options, options);
		this._options = options;
		this._node = options.node;
		this._data = options.data;

		options.width = options.width || Math.floor(this._node.clientWidth);
		options.height = options.height || Math.floor(this._node.clientHeight);
		let margin = options.margin;
		options.innerWidth = options.width - margin.left - margin.right;
		options.innerHeight = options.height - margin.top - margin.bottom;
		return options;
	}

	draw(options) {
		options = this.prepareOptions(options);
		let {node, margin, width, height} = options;
		let {left, top} = margin;

		node.setAttribute('id', _.uniqueId('cm-chart-main-'));
		let canvas = this._canvas = d3.select(node).append('svg')
			.attr('width', width).attr('height', height)
			.append('g').attr('transform', `translate(${left},${top})`);

		let axisSelection = canvas.append('g').classed('cm-axis', true);
		this.drawXAxis(axisSelection);
		this.drawYAxis(axisSelection);

		let chartSelection = canvas.append('g').classed('cm-charts', true);
		this.drawChart(chartSelection);
	}

	get canvas() {
		return this._canvas;
	}

	static plug(...mixins) {
		return mix(this, ...mixins);
	}
}
module.exports = BasicChart;